import { Link } from "react-router-dom";
import { Wallet, Coins, BadgePercent, LogOut, Headphones, ChevronRight, MapPin, Share2, ShieldCheck, Pencil, ShoppingBag, Calendar, FileText } from "lucide-react";
import { inr2 } from "../../lib/api";
import { poweredByBrand } from "../../lib/utils";
import { t } from "../../lib/i18n";
import LangSwitch from "../../components/LangSwitch";
import NotificationPermissionCard from "../../components/NotificationPermissionCard";
import { useIsBlinkit } from "../../lib/storefrontTheme";
import { CONSOLE_BY_ROLE, useProfileController } from "./controllers/useProfileController";
import BkAccountView from "./blinkit/BkAccountView";
import { lseOn } from "../../lib/lse";


export default function ProfilePage() {
    return useIsBlinkit() ? <BkAccountView /> : <ClassicProfilePage />;
}

function ClassicProfilePage() {
    const {
        user, logout, tenant, lang, nav, wallet, coinsValue, showWalletCard, activeConsole, otherRoles, switchTo,
    } = useProfileController();

    if (!user) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center p-6 text-center">
                <h2 className="text-2xl font-display font-bold">{t("login_title", lang)}</h2>
                <p className="text-muted-foreground text-sm mt-2 mb-6">Login to access your orders, wallet, and coupons</p>
                <Link to="/login" data-testid="login-cta" className="bg-primary text-primary-foreground rounded-full px-8 py-3 font-bold">Login / Sign up</Link>
                <div className="mt-10"><LangSwitch /></div>
            </div>
        );
    }

    const items = [
        { Icon: ShoppingBag, label: "My Orders", to: "/orders", testid: "menu-orders" },
        { Icon: Calendar, label: "My Bookings", to: "/orders?tab=bookings", testid: "menu-bookings" },
        { Icon: Wallet, label: t("wallet", lang), to: "/wallet", testid: "menu-wallet", sub: wallet ? `${inr2(wallet.balance)} balance` : "" },
        { Icon: MapPin, label: "Saved addresses", to: "/addresses", testid: "menu-addresses" },
        { Icon: BadgePercent, label: t("coupons", lang), to: "/store-coupons", testid: "menu-coupons" },
        { Icon: Share2, label: "Refer a Shop · Earn", to: "/refer", testid: "menu-refer", sub: "Share your code, earn on every order" },
        { Icon: Headphones, label: "Help & Support", to: "/support", testid: "menu-support" },
        // Location Service Engine Phase 2 (only for tenants with recurring baskets switched on).
        ...(lseOn(tenant, "subscriptions") ? [{ Icon: Calendar, label: "My baskets", to: "/baskets", testid: "menu-baskets", sub: "Weekly / daily groceries in one tap" }] : []),
        { Icon: FileText, label: "Terms & Conditions", to: "/legal/terms", testid: "menu-terms" },
        { Icon: ShieldCheck, label: "Privacy Policy", to: "/legal/privacy", testid: "menu-privacy" },
    ];

    return (
        <div className="pb-32 safe-pb">
            <header className="px-4 pt-5 pb-4 safe-pt flex items-center justify-between bg-card border-b border-border">
                <h1 className="bizil-type-h2 font-display">{t("profile", lang)}</h1>
                <LangSwitch compact />
            </header>

            {/* iter178 — desktop (lg+): two columns, same convention as Cart / Checkout. Below lg both wrappers are plain
                blocks, so phones and tablets render exactly as before. */}
            <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] lg:items-start">
            <div className="min-w-0">
            <section className="m-4 bg-card rounded-2xl border border-border p-5">
                <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold">
                        {(user.first_name?.[0] || user.mobile_number?.[0] || "U").toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="font-bold flex items-center gap-2" data-testid="profile-name">
                            {user.first_name || "Bizil Customer"}
                            <Link to="/profile/edit" className="text-muted-foreground hover:text-primary" data-testid="profile-edit-cta" title="Edit profile"><Pencil size={13} /></Link>
                        </p>
                        <p className="text-sm text-muted-foreground">+91 {user.mobile_number}</p>
                    </div>
                </div>
                {showWalletCard && (
                    <div className="grid grid-cols-2 gap-3 mt-5">
                        <div className="bg-primary/5 border border-primary/10 rounded-xl p-3" data-testid="profile-wallet-balance">
                            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Balance</div>
                            <div className="text-lg font-bold mt-1">{inr2(wallet?.balance || 0)}</div>
                        </div>
                        <div className="bg-accent/10 border border-accent/20 rounded-xl p-3" data-testid="profile-coins-balance">
                            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Coins</div>
                            <div className="text-lg font-bold mt-1 inline-flex items-center gap-1"><Coins size={16} className="text-accent" /> {coinsValue}</div>
                        </div>
                    </div>
                )}
            </section>

            {/* iter134t — push permission control (renders on native only). */}
            <section className="mx-4">
                <NotificationPermissionCard />
            </section>

            {activeConsole && (
                <section className="m-4 bg-primary/5 border border-primary/20 rounded-2xl overflow-hidden" data-testid="active-console-card">
                    <Link
                        to={activeConsole.to}
                        data-testid={`active-console-open-${user.role}`}
                        className="flex items-center gap-4 px-4 py-4 hover:bg-primary/10"
                    >
                        <div className="w-11 h-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0">
                            <activeConsole.Icon size={20} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-[10px] uppercase tracking-wider font-bold text-primary">Your console</p>
                            <p className="font-bold text-sm truncate">{activeConsole.label}</p>
                            <p className="text-xs text-muted-foreground truncate">
                                Open your dashboard for {tenant?.name || "this tenant"}
                            </p>
                        </div>
                        <ChevronRight size={20} className="text-primary flex-shrink-0" />
                    </Link>
                </section>
            )}

            {otherRoles.length > 0 && (
                <section className="m-4 bg-card rounded-2xl border border-border overflow-hidden" data-testid="my-consoles-card">
                    <div className="px-4 pt-3 pb-2 bg-muted/40">
                        <p className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">My Consoles</p>
                        <p className="text-xs text-muted-foreground">You have more roles on this mobile — jump in with one tap.</p>
                    </div>
                    <div className="divide-y divide-border">
                        {otherRoles.map((r) => {
                            const cfg = CONSOLE_BY_ROLE[r.role];
                            return (
                                <button
                                    key={`${r.role}-${r.user_id}`}
                                    onClick={() => switchTo(r)}
                                    data-testid={`console-open-${r.role}`}
                                    className="w-full flex items-center gap-4 px-4 py-4 hover:bg-muted/40 text-left"
                                >
                                    <cfg.Icon size={20} className="text-primary" />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-sm">{cfg.label}</p>
                                        <p className="text-xs text-muted-foreground truncate">
                                            {r.tenant_name || r.tenant_slug || "Platform"}
                                        </p>
                                    </div>
                                    <ChevronRight size={18} className="text-muted-foreground" />
                                </button>
                            );
                        })}
                    </div>
                </section>
            )}

            </div>
            <div className="min-w-0">
            <nav className="m-4 bg-card rounded-2xl border border-border md:grid md:grid-cols-2 md:divide-y-0 divide-y divide-border md:gap-px md:bg-border md:overflow-hidden">
                {items.map(({ Icon, label, to, testid, sub }) => (
                    <Link key={label} to={to} data-testid={testid} className="flex items-center gap-4 px-4 py-4 md:bg-card md:hover:bg-muted/40 md:last:odd:col-span-2 transition">
                        <Icon size={20} className="text-muted-foreground" />
                        <div className="flex-1">
                            <p className="font-semibold text-sm">{label}</p>
                            {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
                        </div>
                        <ChevronRight size={18} className="text-muted-foreground" />
                    </Link>
                ))}
            </nav>

            <button
                data-testid="logout-btn"
                onClick={async () => { try { await logout(); } finally { nav("/"); } }}
                className="mx-4 mt-2 w-[calc(100%-2rem)] bg-card border border-border rounded-2xl py-3 inline-flex items-center justify-center gap-2 font-semibold text-destructive"
            >
                <LogOut size={18} /> {t("logout", lang)}
            </button>
            </div>
            </div>

            <div className="mt-8 text-center" data-testid="tenant-footer">
                <p className="text-sm font-semibold text-foreground/80">{tenant?.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                    Powered by {poweredByBrand(tenant?.branding?.powered_by_text)}
                </p>
            </div>
        </div>
    );
}
